import React, { useState } from "react";
import { useT } from "@/i18n/LanguageContext";
import SectionHeading from "@/components/SectionHeading";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const Contact = () => {
  const { t, lang } = useT();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", type: "general" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error(lang === "hi" ? "सभी फ़ील्ड आवश्यक" : "All fields required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(t.common.error);
    } finally {
      setLoading(false);
    }
  };

  const fieldCls = "bg-white/[0.03] border-white/15 focus-visible:border-[#D4AF37] focus-visible:ring-[#D4AF37]/30 text-white placeholder:text-white/30";

  return (
    <div className="mx-auto max-w-3xl px-4 md:px-8 py-16 md:py-24" data-testid="contact-page">
      <SectionHeading eyebrow="Reach Us Securely" title={t.contact.title} subtitle={t.contact.subtitle} />

      {sent ? (
        <div className="mt-10 surface-card p-10 text-center" data-testid="contact-success">
          <CheckCircle2 className="mx-auto h-10 w-10 text-[#10B981]" />
          <div className="mt-4 text-lg text-white/90">{t.contact.success}</div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 surface-card p-6 md:p-8 space-y-5" data-testid="contact-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.contact.name}</Label>
              <Input value={form.name} onChange={set("name")} className={`mt-2 ${fieldCls}`} data-testid="contact-name-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.contact.email}</Label>
              <Input type="email" value={form.email} onChange={set("email")} className={`mt-2 ${fieldCls}`} data-testid="contact-email-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.contact.subject}</Label>
              <Input value={form.subject} onChange={set("subject")} className={`mt-2 ${fieldCls}`} data-testid="contact-subject-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
                <SelectTrigger className={`mt-2 ${fieldCls}`} data-testid="contact-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/10 text-white">
                  <SelectItem value="general">{t.contact.typeGeneral}</SelectItem>
                  <SelectItem value="media">{t.contact.typeMedia}</SelectItem>
                  <SelectItem value="anon">{t.contact.typeAnon}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-white/60 font-mono-alt">{t.contact.message}</Label>
            <Textarea rows={6} value={form.message} onChange={set("message")} className={`mt-2 resize-none ${fieldCls}`} data-testid="contact-message-input" />
          </div>
          <Button type="submit" disabled={loading} className="w-full h-12 bg-[#D4AF37] text-black hover:bg-yellow-500 font-bold gap-2" data-testid="contact-send-btn">
            <Mail className="h-4 w-4" />
            {loading ? t.common.loading : t.contact.send}
          </Button>
        </form>
      )}
    </div>
  );
};

export default Contact;
